import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  downloadUsageStatsExport,
  fetchAdvisorMessageDistribution,
  fetchMyUsageStats,
  fetchUsageStatsAggregate,
} from '@/redux/asyncThunks/usageStats'
import {
  AdvisorMessageDistributionResponse,
  UsageStatsAggregateResponse,
  UsageStatsPlayer,
} from '@/redux/types/usageStats'

interface UsageStatsState {
  aggregate: UsageStatsAggregateResponse | null
  myStats: UsageStatsPlayer | null
  myTotalRounds: number | null
  isLoadingAggregate: boolean
  isLoadingMine: boolean
  isExporting: boolean
  advisorDistribution: AdvisorMessageDistributionResponse | null
  isLoadingAdvisorDistribution: boolean
  error: string | null
}

const initialState: UsageStatsState = {
  aggregate: null,
  myStats: null,
  myTotalRounds: null,
  isLoadingAggregate: false,
  isLoadingMine: false,
  isExporting: false,
  advisorDistribution: null,
  isLoadingAdvisorDistribution: false,
  error: null,
}

const usageStatsSlice = createSlice({
  name: 'usageStats',
  initialState,
  reducers: {
    clearUsageStatsError: (state) => {
      state.error = null
    },
    updatePlayerAdvisorDistributionDelta: (
      state,
      action: PayloadAction<{
        player_id: string
        chatbot_id: string
        chatbot_name: string
        delta: number
      }>
    ) => {
      if (!state.advisorDistribution) return
      const { player_id, chatbot_id, chatbot_name, delta } = action.payload
      const player = state.advisorDistribution.players.find(
        (p) => String(p.user_id) === player_id
      )
      if (!player) return
      const entry = player.advisor_distribution.find(
        (e) => e.chatbot_id === chatbot_id
      )
      if (entry) {
        entry.message_count += delta
      } else {
        player.advisor_distribution.push({ chatbot_id, chatbot_name, message_count: delta })
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsageStatsAggregate.pending, (state) => {
        state.isLoadingAggregate = true
        state.error = null
      })
      .addCase(fetchUsageStatsAggregate.fulfilled, (state, action) => {
        state.isLoadingAggregate = false
        state.aggregate = action.payload
      })
      .addCase(fetchUsageStatsAggregate.rejected, (state, action) => {
        state.isLoadingAggregate = false
        state.error = action.payload as string
      })
      .addCase(fetchMyUsageStats.pending, (state) => {
        state.isLoadingMine = true
        state.error = null
      })
      .addCase(fetchMyUsageStats.fulfilled, (state, action) => {
        state.isLoadingMine = false
        state.myStats = action.payload.player
        state.myTotalRounds = action.payload.total_rounds
      })
      .addCase(fetchMyUsageStats.rejected, (state, action) => {
        state.isLoadingMine = false
        state.error = action.payload as string
      })
      .addCase(downloadUsageStatsExport.pending, (state) => {
        state.isExporting = true
        state.error = null
      })
      .addCase(downloadUsageStatsExport.fulfilled, (state) => {
        state.isExporting = false
      })
      .addCase(downloadUsageStatsExport.rejected, (state, action) => {
        state.isExporting = false
        state.error = action.payload as string
      })
      .addCase(fetchAdvisorMessageDistribution.pending, (state) => {
        state.isLoadingAdvisorDistribution = true
        state.error = null
      })
      .addCase(fetchAdvisorMessageDistribution.fulfilled, (state, action) => {
        state.isLoadingAdvisorDistribution = false
        state.advisorDistribution = action.payload
      })
      .addCase(fetchAdvisorMessageDistribution.rejected, (state, action) => {
        state.isLoadingAdvisorDistribution = false
        state.error = action.payload as string
      })
  },
})

export const { clearUsageStatsError, updatePlayerAdvisorDistributionDelta } =
  usageStatsSlice.actions
export default usageStatsSlice.reducer
